import { nextTick } from 'vue';
import OperationRecord from '../classes/operation_record.js';
import { useRedoUndoStore } from '../stores/redo_undo_store.js';
import Content from './../classes/content.js';
/**
 * redo, undo処理を行う。
 * @param  {Object} operation_record
 *  {
 *    contents: [ {
 *      key: ,            // sentenceのid
 *      sort_key: ,       // 表示順のkey
 *      tag_type:         // タイプ
 *    } ],
 *    caret_start_key:            // キャレット終了位置のElementのname「content-XX」
 *    caret_end_key:              // キャレット開始位置のElementのname「content-XX」
 *    caret_start:                // キャレットの開始位置
 *    caret_end:                  // キャレットの終了位置
 *    start_separate_index:       // キャレット開始位置のElementのseparate-index
 *    end_separate_index:         // キャレット終了位置のElementのseparate-index
 *    add_line_keys:              // 新しく追加された行のsort_key
 *    delete_line_keys:           // 削除された行のsort_key
 *    is_hashira_created_at_head: // 行の先頭で柱が作成されたかどうか
 *  }
 */
export default {
  data() {
    return {
      maxRecordsLength: 10, // 配列の長さ
    };
  },
  computed: {
    undoRecords() {
      return useRedoUndoStore().undoRecords;
    },
    redoRecords() {
      return useRedoUndoStore().redoRecords;
    },
  },
  methods: {
    /**
     * undo配列にレコードを追加
     * @param  {OperationRecord} record
     *
     */
    pushUndo() {
      let targetDivIndex = 0;
      let targetContentId = '';
      if (!this.selectTo.contentID) {
        const selection = window.getSelection(); // caret位置取得用
        // カーソルがpaper（編集領域）の外にある場合は何もしない
        if (!selection.focusNode) return;

        const targetDiv = selection.focusNode?.dataset?.contentid
          ? selection.focusNode.children[0]
          : selection.focusNode?.dataset?.index
          ? selection.focusNode
          : selection.focusNode.parentNode;

        if (!targetDiv) return;

        const offset = selection.focusOffset > 0 ? 1 : 0;
        targetDivIndex = Number(targetDiv.dataset.index) + offset;
        const parentDiv = targetDiv.parentNode;

        if (!parentDiv?.dataset?.contentid) return;

        targetContentId = this.contentMap.get(parentDiv.dataset.contentid).uuid;
        if (!targetContentId) return;
      } else {
        targetContentId = this.selectTo.contentID;
        targetDivIndex = this.selectTo.wordIndex;
      }
      let firstContent = new Content(this.contents[0]);
      let currentContent = firstContent;
      while (currentContent.next) {
        currentContent.next = new Content(currentContent.next);
        currentContent.next.previous = currentContent;
        currentContent = currentContent.next;
      }
      this.undoRecord.push({
        firstContent: firstContent,
        selectFrom: {
          contentID: this.selectFrom.contentID,
          wordIndex: this.selectFrom.wordIndex,
        },
        selectTo: {
          contentID: this.selectTo.contentID,
          wordIndex: this.selectTo.wordIndex,
        },
        contentID: targetContentId,
        wordIndex: targetDivIndex,
      });
      if (this.undoRecord.length > 10) this.undoRecord.shift();
    },
    /**
     * redo配列にレコードを追加
     * @param  {OperationRecord} record
     *
     */
    pushRedo() {
      let targetDivIndex = 0;
      let targetContentId = '';
      if (!this.selectTo.contentID) {
        const selection = window.getSelection(); // caret位置取得用
        // カーソルがpaper（編集領域）の外にある場合は何もしない
        if (!selection.focusNode) return;

        const targetDiv = selection.focusNode?.dataset?.contentid
          ? selection.focusNode.children[0]
          : selection.focusNode?.dataset?.index
          ? selection.focusNode
          : selection.focusNode.parentNode;

        if (!targetDiv) return;

        const offset = selection.focusOffset > 0 ? 1 : 0;
        targetDivIndex = Number(targetDiv.dataset.index) + offset;
        const parentDiv = targetDiv.parentNode;

        if (!parentDiv?.dataset?.contentid) return;

        targetContentId = this.contentMap.get(parentDiv.dataset.contentid).uuid;
        if (!targetContentId) return;
      } else {
        targetContentId = this.selectTo.contentID;
        targetDivIndex = this.selectTo.wordIndex;
      }
      let firstContent = new Content(this.contents[0]);
      let currentContent = firstContent;
      while (currentContent.next) {
        currentContent.next = new Content(currentContent.next);
        currentContent = currentContent.next;
      }
      this.redoRecord.push({
        firstContent: firstContent,
        selectFrom: {
          contentID: this.selectFrom.contentID,
          wordIndex: this.selectFrom.wordIndex,
        },
        selectTo: {
          contentID: this.selectTo.contentID,
          wordIndex: this.selectTo.wordIndex,
        },
        contentID: targetContentId,
        wordIndex: targetDivIndex,
      });
      if (this.redoRecord.length > 10) this.redoRecord.shift();
    },
    /**
     * redo, undo配列用のレコードを作成
     * @param  {Array} contents
     * @param  {Number} offset
     *
     */
    createOperationParam(contents, params) {
      let param = {
        contents: contents.map((content) => {
          return {
            sort_key: content.sort_key,
            body: content.body,
            tag_type: content.tag_type,
          };
        }),
        caret_start_key: params.caretStartKey,
        caret_end_key: params.caretEndKey,
        caret_start: params.caretStart,
        caret_end: params.caretEnd,
        start_separate_index: params.startSeparateIndex,
        end_separate_index: params.endSeparateIndex,
        add_line_keys: params.addLines,
        delete_line_keys: params.deleteLines,
        is_hashira_created_at_head: params.isHashiraCreatedAtHead,
      };
      return OperationRecord.fromObject(param);
    },
    /**
     * redo配列の初期化
     *
     */
    resetRedoRecords() {
      this.redoRecord = [];
    },
    /**
     * undoの設定
     * @param  {Object} oldContents
     * @param  {Number} sort_key
     */
    undoSetting(oldContents, params = {}) {
      params.caretStartKey ||= this.caretPositionName;
      params.caretEndKey ||= params.caretStartKey;
      if (params.caretStart == null) params.caretStart = this.caretStartOffset;
      if (params.caretEnd == null) params.caretEnd = this.caretEndOffset;
      if (params.startSeparateIndex == null) params.startSeparateIndex = 0;
      if (params.endSeparateIndex == null) params.endSeparateIndex = params.startSeparateIndex;
      params.addLines ||= null;
      params.deleteLines ||= null;
      params.isHashiraCreatedAtHead ||= false;
      const operationParams = this.createOperationParam(oldContents, params);
      this.pushUndo(operationParams);
      this.resetRedoRecords();
    },
    /**
     * redo, undoの実行
     * @param  {String} type ["redo", "undo"]
     *
     */
    recordOperation(type = null) {
      // redo, undo配列から更新用のレコードを取得
      let targetRecord = {};
      if (type == 'undo') {
        if (this.undoRecords.length == 0) return;
        targetRecord = this.undoRecords.pop();
        this.getCaretPosition();
      } else if (type == 'redo') {
        if (this.redoRecords.length == 0) return;
        targetRecord = this.redoRecords.pop();
      } else {
        return;
      }

      let beforeOperationParams = []; // redo/undo処理前のパラメータ
      let targetContent = new Object(); // redo/undo処理するcontent

      // 変更が必要なcontentsについて forEachでそれぞれを処理する
      targetRecord.contents.forEach((content) => {
        targetContent = this.getContentBySortKey(content.sort_key);

        // 現状の this.contents 配列に該当ソートキーがある場合
        if (targetContent != undefined) {
          // 後ほど、処理を行う前の状態をredo/undo配列に追加するために
          // 処理前のパラメータを一時的に保存
          beforeOperationParams.push({
            sort_key: targetContent.sort_key,
            body: targetContent.body,
            tag_type: targetContent.tag_type,
          });

          // redo/undo配列から 既存のcontentのbodyを更新
          // content要素の増減はないのでbodyを変更するのみ
          targetContent.body = content.body;
          targetContent.status = 'editing';
        } else {
          // 現状の this.contents 配列に該当ソートキーがない場合
          // 新しいcontentを ソートキーの位置に追加する必要がある

          // redo/undo配列から 新規追加用パラメータ作成
          let new_sentence_param = {
            key: null,
            body: content.body,
            tag_type: content.tagType,
            sort_key: content.sort_key,
            separate: [],
            status: 'editing',
          };
          targetContent = new Content(new_sentence_param);

          // 新しいcontentを追加
          this.contents.push(targetContent);
        }

        // tag_type変更
        if (targetContent.tag_type != content.tag_type) targetContent.changeTagType(content.tag_type);
      });

      // 削除が必要なcontentについての処理
      if (targetRecord.add_line_keys != undefined && !targetRecord.is_hashira_created_at_head) {
        targetRecord.add_line_keys.forEach((key) => {
          // 現状の this.contents 配列から 該当contentの削除
          let index = this.contents.findIndex(({ sort_key }) => sort_key == key);
          let deleteContent = this.contents.splice(index, 1)[0];

          // 削除処理前のパラメータを一時的に保存
          beforeOperationParams.push({
            sort_key: key,
            body: deleteContent.body,
            tag_type: deleteContent.tag_type,
          });
        });
      }

      // 処理を行う前の状態を undoのときはredo配列に、redoのときはundo配列に追加
      const selection = window.getSelection();

      // カーソルがpaper（編集領域）の外にある場合は処理をスキップ
      if (!selection.focusNode || !selection.anchorNode) return;

      let range = selection.getRangeAt(0);
      // selection.anchorNode.firstElementChildがundefinedの場合は、parentElementを参照
      let caretStartElement = selection.anchorNode.firstElementChild
        ? selection.anchorNode.firstElementChild
        : selection.anchorNode.parentElement;
      let caretEndElement = selection.focusNode.firstElementChild
        ? selection.focusNode.firstElementChild
        : selection.focusNode.parentElement;

      if (!caretStartElement || !caretEndElement) return;
      if (selection.anchorNode.className == 'hashira') {
        caretStartElement = selection.anchorNode;
        caretEndElement = selection.focusNode;
      }
      const operationParams = {
        contents: beforeOperationParams,
        caret_start_key:
          caretStartElement.getAttribute('name') || caretStartElement.lastElementChild.getAttribute('name'), // 柱の場合、階層が異なる
        caret_end_key: caretEndElement.getAttribute('name') || caretEndElement.lastElementChild.getAttribute('name'), // 柱の場合、階層が異なる
        caret_start: range.startOffset,
        caret_end: range.endOffset,
        start_separate_index: caretStartElement.getAttribute('separate-index'),
        end_separate_index: caretEndElement.getAttribute('separate-index'),
        add_line_keys: targetRecord.delete_line_keys,
        delete_line_keys: targetRecord.add_line_keys,
        is_hashira_created_at_head: targetRecord.is_hashira_created_at_head,
      };
      if (type == 'undo') {
        this.pushRedo(operationParams);
      } else if (type == 'redo') {
        this.pushUndo(operationParams);
      }

      // vueの再描画を待って、caret位置を再取得
      nextTick(() => {
        this.setHashiraIndex();
        let startSeparateIndex = targetRecord.start_separate_index || 0;
        let endSeparateIndex = targetRecord.end_separate_index || 0;
        const caretStartElement = document.getElementsByName(targetRecord.caret_start_key)[startSeparateIndex];
        const caretEndElement = document.getElementsByName(targetRecord.caret_end_key)[endSeparateIndex];
        selection.removeAllRanges();
        range = document.createRange();
        if (caretStartElement) range.setStart(caretStartElement.firstChild, targetRecord.caret_start);
        if (caretEndElement) range.setEnd(caretEndElement.firstChild, targetRecord.caret_end);
        selection.addRange(range);
        this.getCaretPosition();
        // 取得したcaret位置は、updateTagTypeの際にcaret位置を更新する
      });
      // リロード前に確認モーダルを出す
      this.addConfirmEvent();
    },
  },
};
